import { z } from "zod";
import {
  MAX_TITLE_CENTS,
  NFSE_CTRIBNAC_RE,
  NFSE_IMMUNITIES,
  NFSE_MAX_DESCRIPTION,
  NFSE_NATURES,
  NFSE_PROVIDER_MODES,
  NFSE_SPECIAL_REGIMES,
  nfseCancelReasonProblem,
  normalizeEventText,
  type NfseNature,
  type NfseProviderMode,
} from "@cx/domain";
import { listQuery, type ListResult } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import type { FiscalProviderId } from "./fiscal";

// ===== NFS-e (T2.8, RF-SER-04): DPS do Sistema Nacional ou da prefeitura pela Nuvem Fiscal =====

export const SERVICE_INVOICE_STATUSES = [
  "draft",
  "queued",
  "sending",
  "authorized",
  "rejected",
  "canceled",
] as const;
export type ServiceInvoiceStatus = (typeof SERVICE_INVOICE_STATUSES)[number];

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(MAX_TITLE_CENTS, "Valor acima do limite");
const pct4 = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Percentual inválido")
    .min(0, "Percentual de 0 a 100%")
    .max(1_000_000, "Percentual de 0 a 100%");
const digits = (n: number, what: string) =>
  z
    .string()
    .trim()
    .transform((v) => v.replace(/\D/g, ""))
    .pipe(z.string().regex(new RegExp(`^\\d{${n}}$`), `${what} com ${n} dígitos`));

/** Serviço prestado: código de tributação nacional (LC 116 + desdobro), municipal, CNAE, NBS e descrição. */
export const nfseServiceInput = z.object({
  cTribNac: z
    .string("Informe o código de tributação nacional")
    .trim()
    .transform((v) => v.replace(/\D/g, ""))
    .pipe(z.string().regex(NFSE_CTRIBNAC_RE, "Código de tributação nacional com 6 dígitos (ex.: 010701)")),
  cTribMun: text(20),
  cnae: nullish(digits(7, "CNAE")),
  nbs: nullish(digits(9, "NBS")),
  description: z
    .string("Descreva o serviço")
    .trim()
    .min(1, "Descreva o serviço")
    .max(NFSE_MAX_DESCRIPTION, `Até ${NFSE_MAX_DESCRIPTION} caracteres`),
});
export type NfseServiceInput = z.output<typeof nfseServiceInput>;

export const nfseRetentionsInput = z.object({
  pisPct4: pct4("o PIS").default(0),
  cofinsPct4: pct4("a COFINS").default(0),
  csllPct4: pct4("a CSLL").default(0),
  irPct4: pct4("o IR").default(0),
  inssPct4: pct4("o INSS").default(0),
});

/** Rascunho da NFS-e avulsa (ou editado depois de gerado pela cobrança do contrato). */
export const serviceInvoiceInput = z
  .object({
    contactId: ulidParam,
    service: nfseServiceInput,
    nature: z.literal(NFSE_NATURES, "Natureza inválida").default(1),
    immunityType: nullish(z.literal(NFSE_IMMUNITIES, "Tipo de imunidade inválido")),
    exportCountry: nullish(
      z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/, "País com 2 letras (ISO)"),
    ),
    servicesCents: cents("o valor dos serviços").min(1, "Informe o valor dos serviços"),
    discountCents: cents("o desconto").default(0),
    deductionsCents: cents("as deduções").default(0),
    issPct4: nullish(pct4("a alíquota do ISS")),
    issWithheld: z.boolean().default(false),
    retentions: nfseRetentionsInput.default({
      pisPct4: 0,
      cofinsPct4: 0,
      csllPct4: 0,
      irPct4: 0,
      inssPct4: 0,
    }),
    complementaryInfo: text(2000),
  })
  .superRefine((v, c) => {
    if (v.nature === 2 && v.immunityType === null)
      c.addIssue({ code: "custom", path: ["immunityType"], message: "Informe o tipo da imunidade" });
    if (v.nature === 3 && v.exportCountry === null)
      c.addIssue({ code: "custom", path: ["exportCountry"], message: "Informe o país do resultado" });
    if (v.discountCents + v.deductionsCents > v.servicesCents)
      c.addIssue({ code: "custom", path: ["discountCents"], message: "Desconto e deduções passam do valor" });
  });
export type ServiceInvoiceInput = z.output<typeof serviceInvoiceInput>;
export type ServiceInvoiceInputBody = z.input<typeof serviceInvoiceInput>;

export const serviceInvoiceListQuery = listQuery.extend({
  status: z.enum(SERVICE_INVOICE_STATUSES).optional(),
});
export type ServiceInvoiceListQuery = z.output<typeof serviceInvoiceListQuery>;

export const nfseCancelInput = z.object({
  reason: z
    .string("Informe o motivo")
    .max(1000, "Texto longo demais")
    .superRefine((v, c) => {
      const p = nfseCancelReasonProblem(v);
      if (p) c.addIssue({ code: "custom", message: p });
    })
    .transform(normalizeEventText),
  /** Código do cancelamento exigido por algumas prefeituras (ex.: 1 erro na emissão, 2 serviço não prestado). */
  code: nullish(
    z
      .string()
      .trim()
      .regex(/^[0-9A-Za-z]{1,4}$/, "Código inválido"),
  ),
});
export type NfseCancelInput = z.output<typeof nfseCancelInput>;

export type NfseRecipient = {
  name: string;
  document: string;
  im: string | null;
  email: string | null;
  phone: string | null;
  address: {
    street: string;
    number: string;
    complement: string | null;
    district: string;
    city: string;
    uf: string;
    cityIbge: string;
    cep: string | null;
  } | null;
};

export type ServiceInvoiceListItem = {
  id: string;
  status: ServiceInvoiceStatus;
  environment: 1 | 2;
  nfseNumber: string | null;
  rpsNumber: string | null;
  rpsSeries: string | null;
  issuedAt: string | null;
  recipientName: string;
  servicesCents: number;
  netCents: number;
  origin:
    | {
        type: "contract_billing";
        id: string;
        contractId: string;
        contractNumber: number;
        competence: string;
      }
    /** Ordem de serviço (T2.9): a NFS-e dos serviços. */
    | { type: "service_order"; id: string; number: number }
    | null;
  createdAt: string;
};
export type ServiceInvoiceList = ListResult<ServiceInvoiceListItem>;

export type ServiceInvoiceEventView = {
  id: string;
  kind: "cancel";
  status: "pending" | "sending" | "registered" | "rejected";
  reason: string;
  code: string | null;
  statusCode: string | null;
  statusMessage: string | null;
  registeredAt: string | null;
  canRefresh: boolean;
  createdAt: string;
};

export type ServiceInvoiceView = ServiceInvoiceListItem &
  Omit<ServiceInvoiceInput, "contactId"> & {
    contactId: string | null;
    recipient: NfseRecipient;
    providerMode: NfseProviderMode;
    attempt: number;
    verificationCode: string | null;
    linkUrl: string | null;
    authorizedAt: string | null;
    canceledAt: string | null;
    rejectionCode: string | null;
    rejectionReason: string | null;
    statusMessage: string | null;
    editable: boolean;
    canRefresh: boolean;
    canCancel: boolean;
    hasXml: boolean;
    hasPdf: boolean;
    values: {
      baseCents: number;
      issCents: number;
      issWithheldCents: number;
      retentionsCents: number;
      pisCents: number;
      cofinsCents: number;
      csllCents: number;
      irCents: number;
      inssCents: number;
      netCents: number;
    };
    events: ServiceInvoiceEventView[];
  };

/** Configuração da NFS-e da empresa (Preferências › Nota fiscal de serviço). */
export const nfseSettingsInput = z.object({
  environment: z.literal([1, 2], "Ambiente inválido"),
  providerMode: z.enum(NFSE_PROVIDER_MODES, "Padrão de emissão inválido").default("nacional"),
  specialRegime: z.literal(NFSE_SPECIAL_REGIMES, "Regime especial inválido").default(0),
  rpsSeries: z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z]{1,5}$/, "Série do RPS com até 5 letras/números")
    .default("1"),
  rpsNextNumber: z.number().int().min(1, "Número de 1 a 999.999.999").max(999_999_999).default(1),
  service: nullish(nfseServiceInput),
  issPct4: nullish(pct4("a alíquota do ISS")),
  issWithheld: z.boolean().default(false),
  retentions: nfseRetentionsInput.default({ pisPct4: 0, cofinsPct4: 0, csllPct4: 0, irPct4: 0, inssPct4: 0 }),
  pisCofinsCst: z
    .string()
    .regex(/^\d{2}$/, "CST com 2 dígitos")
    .default("01"),
});
export type NfseSettingsInput = z.output<typeof nfseSettingsInput>;
export type NfseSettings = NfseSettingsInput & {
  provider: FiscalProviderId;
  /** RPS já cadastrado no provedor: série e número não mudam mais por aqui. */
  registered: boolean;
  /** Configuração salva ao menos uma vez (sem ela a NFS-e não sai). */
  configured: boolean;
};

export type { NfseNature, NfseProviderMode };
